import { deviceCodeSchema, type ApproveDisplayRequestResponse } from '@bingo/shared';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api.js';

/**
 * Destino do QR da TV — mobile-first, de propósito.
 *
 * Quem abre esta tela está em pé, com o celular numa mão, olhando para a TV. É
 * a primeira experiência do produto para uma família, então ela tem o mínimo:
 * entrar, escolher o que jogar, pronto. "Jogo rápido" existe para quem não quer
 * criar evento nenhum — só quer jogar agora.
 */

interface EventSummary {
  id: string;
  name: string;
  hasOpenGame: boolean;
}

export function Parear() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [code, setCode] = useState((params.get('c') ?? '').toUpperCase());
  const [events, setEvents] = useState<EventSummary[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api<EventSummary[]>('/api/events')
      .then(setEvents)
      .catch(() => setEvents([]));
  }, []);

  const approve = async (body: Record<string, unknown>) => {
    const parsed = deviceCodeSchema.safeParse(code);
    if (!parsed.success) {
      setError('Confira o código que está na TV.');
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const result = await api<ApproveDisplayRequestResponse>(
        `/api/auth/display/requests/${parsed.data}/approve`,
        { method: 'POST', body },
      );

      // O celular vira o controle do evento que acabou de aparecer na TV.
      navigate(`/controle?eventId=${result.eventId}`, { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível conectar a TV.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mobile">
      <h1>Conectar a TV</h1>

      <label className="field">
        Código na TV
        <input
          value={code}
          onChange={(event) => setCode(event.target.value.toUpperCase().slice(0, 6))}
          inputMode="text"
          autoCapitalize="characters"
          autoComplete="off"
          placeholder="ABC123"
          className="code-input"
        />
      </label>

      {error ? <p className="error">{error}</p> : null}

      <button
        type="button"
        className="btn primary big"
        disabled={busy}
        onClick={() => void approve({ quickGame: { eventName: 'Bingo em família' } })}
      >
        Jogo rápido
      </button>
      <p className="hint-text">Cria um bingo novo e já começa. Cartela cheia, centro livre.</p>

      {events && events.length > 0 ? (
        <>
          <h2>Ou escolha um evento</h2>
          <div className="event-list">
            {events.map((event) => (
              <button
                key={event.id}
                type="button"
                className="btn event"
                disabled={busy}
                onClick={() => void approve({ eventId: event.id })}
              >
                <span>{event.name}</span>
                {event.hasOpenGame ? <em>em andamento</em> : null}
              </button>
            ))}
          </div>
        </>
      ) : null}
    </main>
  );
}
